import { Icon } from '@iconify/react'
import {
  MAX_URL_ATTRIBUTION_SEARCH_TERMS,
  MAX_URL_ATTRIBUTION_SEARCH_TERM_LENGTH,
  URL_ATTRIBUTION_CONVERSION_FILTERS,
  hasUrlAttributionSearchDraft,
  type UrlAttributionConversionFilter,
} from '@/lib/campaign-url-attribution-view'
import styles from './CampaignDashboard.module.css'

interface CampaignUrlAttributionToolbarProps {
  dateFrom: string
  dateTo: string
  onDateFromChange: (value: string) => void
  onDateToChange: (value: string) => void
  onApplyPeriod: () => void
  canApplyPeriod: boolean
  conversionFilter: UrlAttributionConversionFilter
  onConversionFilterChange: (value: UrlAttributionConversionFilter) => void
  searchDraft: string
  onSearchDraftChange: (value: string) => void
  searchTerms: readonly string[]
  onAddSearchTerms: () => void
  onRemoveSearchTerm: (term: string) => void
  hideChartUrls: boolean
  onToggleHideChartUrls: () => void
  onClearLocalFilters: () => void
  hasLocalFilters: boolean
}

function isConversionFilter(value: string): value is UrlAttributionConversionFilter {
  return URL_ATTRIBUTION_CONVERSION_FILTERS.some((option) => option.value === value)
}

export function CampaignUrlAttributionToolbar({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onApplyPeriod,
  canApplyPeriod,
  conversionFilter,
  onConversionFilterChange,
  searchDraft,
  onSearchDraftChange,
  searchTerms,
  onAddSearchTerms,
  onRemoveSearchTerm,
  hideChartUrls,
  onToggleHideChartUrls,
  onClearLocalFilters,
  hasLocalFilters,
}: CampaignUrlAttributionToolbarProps) {
  const canAddSearchTerm =
    hasUrlAttributionSearchDraft(searchDraft) &&
    searchTerms.length < MAX_URL_ATTRIBUTION_SEARCH_TERMS

  return (
    <div className={styles.chartToolbar}>
      <div className={styles.urlAttributionFilters}>
        <div className={styles.customFilters}>
          <div className={styles.fieldGroup}>
            <label htmlFor="url-attribution-date-from">De</label>
            <input
              id="url-attribution-date-from"
              type="date"
              className={styles.dateInput}
              value={dateFrom}
              onChange={(event) => onDateFromChange(event.target.value)}
            />
          </div>
          <div className={styles.fieldGroup}>
            <label htmlFor="url-attribution-date-to">Até</label>
            <input
              id="url-attribution-date-to"
              type="date"
              className={styles.dateInput}
              value={dateTo}
              onChange={(event) => onDateToChange(event.target.value)}
            />
          </div>
          <button
            type="button"
            className={styles.actionButton}
            onClick={onApplyPeriod}
            disabled={!canApplyPeriod}
          >
            Aplicar período
          </button>
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor="url-attribution-conversion">Conversão</label>
          <select
            id="url-attribution-conversion"
            className={styles.urlFilterSelect}
            value={conversionFilter}
            onChange={(event) => {
              if (isConversionFilter(event.target.value)) {
                onConversionFilterChange(event.target.value)
              }
            }}
          >
            {URL_ATTRIBUTION_CONVERSION_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className={`${styles.fieldGroup} ${styles.urlAttributionSearch}`}>
          <label htmlFor="url-attribution-search">Buscar página</label>
          <div className={styles.urlAttributionSearchField}>
            <input
              id="url-attribution-search"
              type="search"
              className={styles.searchInput}
              placeholder="Digite um termo e pressione Enter"
              maxLength={MAX_URL_ATTRIBUTION_SEARCH_TERM_LENGTH * 4}
              value={searchDraft}
              onChange={(event) => onSearchDraftChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') {
                  return
                }
                event.preventDefault()
                onAddSearchTerms()
              }}
            />
            <button
              type="button"
              className={styles.filterChip}
              onClick={onAddSearchTerms}
              disabled={!canAddSearchTerm}
            >
              <Icon icon="mdi:plus" />
              Adicionar
            </button>
          </div>
          {searchTerms.length > 0 ? (
            <div className={styles.urlAttributionSearchTerms} role="list" aria-label="Termos de busca">
              {searchTerms.map((term) => (
                <span key={term} className={styles.urlAttributionSearchTerm} role="listitem">
                  {term}
                  <button
                    type="button"
                    className={styles.urlAttributionSearchTermRemove}
                    aria-label={`Remover termo ${term}`}
                    onClick={() => onRemoveSearchTerm(term)}
                  >
                    <Icon icon="mdi:close" />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          {searchTerms.length > 1 ? (
            <p className={styles.urlAttributionSearchHint}>
              Mostra origens que tenham qualquer um destes termos.
            </p>
          ) : null}
        </div>

        <div className={styles.urlAttributionActions}>
          <button
            type="button"
            className={hideChartUrls ? styles.filterChipActive : styles.filterChip}
            aria-pressed={hideChartUrls}
            onClick={onToggleHideChartUrls}
          >
            <Icon icon={hideChartUrls ? 'mdi:eye' : 'mdi:eye-off-outline'} />
            {hideChartUrls ? 'Mostrar URLs no gráfico' : 'Ocultar URLs no gráfico'}
          </button>
          <button
            type="button"
            className={styles.filterChip}
            onClick={onClearLocalFilters}
            disabled={!hasLocalFilters}
          >
            <Icon icon="mdi:filter-off-outline" />
            Limpar filtros
          </button>
        </div>
      </div>
    </div>
  )
}
